import CodeIcon from "@mui/icons-material/Code";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import ImageIcon from "@mui/icons-material/Image";
import LinkIcon from "@mui/icons-material/Link";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import RedoIcon from "@mui/icons-material/Redo";
import TableChartIcon from "@mui/icons-material/TableChart";
import UndoIcon from "@mui/icons-material/Undo";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Divider from "@mui/material/Divider";
import { Editor } from "@tiptap/react";
import { useState } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { CirclePicker } from "react-color";
import { ChevronDown } from "src/icons/chevron-down";
import { ChevronUp } from "src/icons/chevron-up";
import { MagicWandIcon } from "src/icons/magic-wand-icon";

type Level = 1 | 2 | 3 | 4 | 5 | 6;

interface Props {
  editor: Editor;
  handleAidropdown: () => void;
  evaluationData: any;
  isEEATMode: boolean;
  isWritingMode?: boolean;
  onToggleWritingMode?: () => void;
}

const MenuBar = ({
  editor,
  handleAidropdown,
  evaluationData,
  isEEATMode,
  isWritingMode = false,
  onToggleWritingMode,
}: Props) => {
  const theme = useTheme();
  const useCompactToolbar = useMediaQuery("(max-width:1250px)");
  const [headingsAnchorEl, setHeadingsAnchorEl] = useState<null | HTMLElement>(null);
  const [colorAnchorEl, setColorAnchorEl] = useState<null | HTMLElement>(null);
  const [alignAnchorEl, setAlignAnchorEl] = useState<null | HTMLElement>(null);
  const openHeadingsDialog = Boolean(headingsAnchorEl);
  const openTextColorDialog = Boolean(colorAnchorEl);
  const openAlignDialog = Boolean(alignAnchorEl);
  const [openLinkDialog, setOpenLinkDialog] = useState(false);
  const [link, setLink] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [selectedColor, setSelectedColor] = useState("#000000");
  const [isExplicitParagraph, setIsExplicitParagraph] = useState(false);
  const [showSelectionTooltip, setShowSelectionTooltip] = useState(false);
  const [tableAnchorEl, setTableAnchorEl] = useState<null | HTMLElement>(null);
  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);
  const [tableDimensions, setTableDimensions] = useState({ rows: 0, cols: 0 });
  const [hoveredDimensions, setHoveredDimensions] = useState({
    rows: 0,
    cols: 0,
  });
  const maxRows = 8;
  const maxCols = 8;
  const isMoreMenuOpen = Boolean(moreAnchorEl);

  if (!editor) {
    return null;
  }

  const handleTableButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setTableAnchorEl(event.currentTarget);
  };

  const handleTablePopoverClose = () => {
    setTableAnchorEl(null);
    setHoveredDimensions({ rows: 0, cols: 0 });
  };

  const handleCellHover = (row: number, col: number) => {
    setHoveredDimensions({ rows: row + 1, cols: col + 1 });
  };

  const handleTableInsert = () => {
    if (hoveredDimensions.rows > 0 && hoveredDimensions.cols > 0) {
      editor
        .chain()
        .focus()
        .insertTable({
          rows: hoveredDimensions.rows,
          cols: hoveredDimensions.cols,
          withHeaderRow: true,
        })
        .run();
    }
    handleTablePopoverClose();
  };

  const handleMoreMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMoreAnchorEl(event.currentTarget);
  };

  const handleMoreMenuClose = () => {
    setMoreAnchorEl(null);
  };

  return (
    <Box
      data-testid="editor-toolbar"
      sx={{
        display: "flex",
        justifyContent: useCompactToolbar ? "flex-start" : "center",
        alignItems: "center",
        width: "100%",
        px: 1,
        py: 0.5,
        zIndex: 99999,
        backgroundColor: "rgba(255, 255, 255, 0.94)",
        backdropFilter: "blur(6px)",
        overflowX: "auto",
        overflowY: "hidden",
        whiteSpace: "nowrap",
        position: "relative",
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": {
          height: "6px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(123, 133, 173, 0.45)",
          borderRadius: "8px",
        },
      }}
    >
      <ButtonGroup
        variant="outlined"
        aria-label="text formatting options"
        size="small"
        sx={{
          display: "inline-flex",
          flexWrap: "nowrap",
          minWidth: "max-content",
          overflow: "visible !important",
          "& .MuiButtonGroup-grouped": {
            backgroundColor: "white !important",
            border: "none !important",
            borderRadius: "6px !important",
            boxShadow: "none !important",
            padding: "4px 8px !important",
          },

          // width: "100%",
        }}
      >
        {/* Text Formatting */}
        <Box sx={{ position: "relative" }}>
          <Button
            onClick={() => editor.chain().focus().undo().run()}
            sx={{
              color: "#333333",
            }}
          >
            <UndoIcon />
          </Button>
          <Button
            onClick={() => editor.chain().focus().redo().run()}
            sx={{
              color: "#333333",
            }}
          >
            <RedoIcon />
          </Button>
        </Box>
        {/* /divider*/}
        <Box
          sx={{
            backgroundColor: "white",
          }}
        >
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              borderColor: "#D6DBE8 !important",

              height: "30px !important",
              margin: "0px 4px !important",
            }}
          />
        </Box>
        <Button
          data-testid="menu-aa-toggle"
          onClick={(e) => setHeadingsAnchorEl(headingsAnchorEl ? null : e.currentTarget)}
          sx={{
            color: openHeadingsDialog ? "blue" : "#333333",
          }}
        >
          {editor.isActive("paragraph") && !editor.isActive("heading") && (
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: "700",
                marginRight: "4px",
                color: "#333333",
              }}
            >
              A{" "}
              <span style={{ marginLeft: "-4px", fontSize: "12px" }}>a</span>
            </Typography>
          )}
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: "700",
              color: "#333333",
            }}
          >
            {editor.isActive("heading", { level: 1 }) && "H1"}
            {editor.isActive("heading", { level: 2 }) && "H2"}
            {editor.isActive("heading", { level: 3 }) && "H3"}
            {editor.isActive("heading", { level: 4 }) && "H4"}
            {editor.isActive("heading", { level: 5 }) && "H5"}
            {editor.isActive("heading", { level: 6 }) && "H6"}
          </Typography>
          {openHeadingsDialog ? <ChevronUp /> : <ChevronDown />}
        </Button>
        <Popover
          open={openHeadingsDialog}
          anchorEl={headingsAnchorEl}
          onClose={() => setHeadingsAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          data-testid="menu-aa-panel"
          PaperProps={{
            sx: {
              mt: "6px",
              backgroundColor: "white",
              borderRadius: "8px",
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
              width: "160px",
              py: 0.5,
            },
          }}
        >
          <Button
            fullWidth
            onClick={() => {
              editor.chain().focus().setParagraph().run();
              setIsExplicitParagraph(true);
              setHeadingsAnchorEl(null);
            }}
            sx={{
              color: "#333333",
              justifyContent: "flex-start",
              border: "none",
              borderRadius: 0,
              px: 2,
              py: 0.75,
              fontSize: "14px",
              textTransform: "none",
              "&:hover": { backgroundColor: "#f5f5f5" },
            }}
          >
            P &nbsp; Paragraph
          </Button>

          {[1, 2, 3, 4, 5, 6].map((level) => (
            <Button
              fullWidth
              key={`heading-option-${level}`}
              onClick={() => {
                editor
                  .chain()
                  .focus()
                  .toggleHeading({ level: level as Level })
                  .run();
                setHeadingsAnchorEl(null);
              }}
              sx={{
                color: "#333333",
                justifyContent: "flex-start",
                border: "none",
                borderRadius: 0,
                px: 2,
                py: 0.75,
                fontSize: "14px",
                textTransform: "none",
                "&:hover": { backgroundColor: "#f5f5f5" },
              }}
            >
              H
              <span style={{ fontSize: "12px", marginTop: "4px" }}>
                {level}
              </span>
              &nbsp;&nbsp;Heading {level}
            </Button>
          ))}
        </Popover>
        {/* /divider*/}
        <Box
          sx={{
            backgroundColor: "white",
          }}
        >
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              border: "1px solid #E0E0E0",
              borderColor: "gray !important",

              height: "30px !important",
              margin: "0px 4px !important",
            }}
          />
        </Box>
        <Button
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Bold"
          sx={{
            color: editor.isActive("bold") ? "blue" : "#333333",
          }}
        >
          <FormatBoldIcon />
        </Button>
        <Button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Italic"
          sx={{
            color: editor.isActive("italic") ? "blue" : "#333333",
          }}
        >
          <FormatItalicIcon />
        </Button>
        <Button
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title="Underline"
          sx={{
            color: editor.isActive("underline") ? "blue" : "#333333",
          }}
        >
          <FormatUnderlinedIcon />
        </Button>
        <Button
          onClick={() => editor.chain().focus().unsetAllMarks().run()}
          title="Clear Formatting"
        >
          <FormatClearIcon
            sx={{
              color: "#333333",
            }}
          />
        </Button>
        {/* text color */}
        <Button
          data-testid="menu-color-toggle"
          onClick={(e) => {
            setColorAnchorEl(colorAnchorEl ? null : e.currentTarget);
            setSelectedColor("#000000");
          }}
          sx={{
            color: "#333333",
          }}
          title="Text Color"
        >
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: "700",
              textDecoration: "underline",
              textDecorationColor: selectedColor,
              textDecorationThickness: "4px",
              textUnderlineOffset: "3px",
              textDecorationStyle: "solid",
            }}
          >
            A
          </Typography>
          {openTextColorDialog ? <ChevronUp /> : <ChevronDown />}
        </Button>
        <Popover
          open={openTextColorDialog}
          anchorEl={colorAnchorEl}
          onClose={() => setColorAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          data-testid="menu-color-panel"
          PaperProps={{
            sx: {
              mt: "6px",
              backgroundColor: "white",
              borderRadius: "8px",
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
              width: "250px",
              padding: "0.75rem",
            },
          }}
        >
          <CirclePicker
            color={selectedColor}
            onChangeComplete={(color) => {
              setSelectedColor(color.hex);
              editor.chain().focus().setColor(color.hex).run();
            }}
          />
        </Popover>
        {/* /divider*/}
        <Box
          sx={{
            backgroundColor: "white",
          }}
        >
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              border: "1px solid #E0E0E0",
              borderColor: "gray !important",

              height: "30px !important",
              margin: "0px 4px !important",
            }}
          />
        </Box>

        {/* align */}
        <Button
          data-testid="menu-align-toggle"
          onClick={(e) => setAlignAnchorEl(alignAnchorEl ? null : e.currentTarget)}
          sx={{
            color: openAlignDialog ? "blue" : "#333333",
          }}
        >
          <FormatAlignLeftIcon />
          {openAlignDialog ? <ChevronUp /> : <ChevronDown />}
        </Button>
        <Popover
          open={openAlignDialog}
          anchorEl={alignAnchorEl}
          onClose={() => setAlignAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          data-testid="menu-align-panel"
          PaperProps={{
            sx: {
              mt: "6px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "white",
              borderRadius: "8px",
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
              p: 0.5,
            },
          }}
        >
          <Button
            onClick={() =>
              editor.chain().focus().setTextAlign("left").run()
            }
            sx={{
              color: "#333333",
              border: "none",
              minWidth: "36px",
              p: 1,
              "&:hover": { backgroundColor: "#f5f5f5" },
            }}
          >
            <FormatAlignLeftIcon />
          </Button>
          <Button
            onClick={() =>
              editor.chain().focus().setTextAlign("center").run()
            }
            sx={{
              color: "#333333",
              border: "none",
              minWidth: "36px",
              p: 1,
              "&:hover": { backgroundColor: "#f5f5f5" },
            }}
            title="Align Center"
          >
            <FormatAlignCenterIcon />
          </Button>
          <Button
            onClick={() =>
              editor.chain().focus().setTextAlign("right").run()
            }
            title="Align Right"
            sx={{
              color: "#333333",
              border: "none",
              minWidth: "36px",
              p: 1,
              "&:hover": { backgroundColor: "#f5f5f5" },
            }}
          >
            <FormatAlignRightIcon />
          </Button>
        </Popover>

        {/* Lists */}
        <Button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Bullet List"
          sx={{
            color: editor.isActive("bulletList") ? "blue" : "#333333",
          }}
        >
          <FormatListBulletedIcon />
        </Button>
        <Button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Numbered List"
          sx={{
            color: editor.isActive("orderedList") ? "blue" : "#333333",
          }}
        >
          <FormatListNumberedIcon />
        </Button>
        {/* add link button  */}
        <Button
          onClick={() => setOpenLinkDialog(true)}
          title="Add Link"
          sx={{ color: editor.isActive("link") ? "blue" : "#333333" }}
        >
          <LinkIcon />
        </Button>
        {/* add image button */}
        {!useCompactToolbar && (
          <Button
            onClick={() => setOpenImageDialog(true)}
            title="Insert Image"
            sx={{ color: "#333333" }}
          >
            <ImageIcon />
          </Button>
        )}

        {/* /divider*/}
        <Box
          sx={{
            backgroundColor: "white",
          }}
        >
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              border: "1px solid #E0E0E0",
              borderColor: "gray !important",

              height: "30px !important",
              margin: "0px 4px !important",
            }}
          />
        </Box>
        {/* code */}
        {!useCompactToolbar && (
          <Button
            onClick={() => editor.chain().focus().toggleCode().run()}
            title="Code"
            sx={{
              color: editor.isActive("code") ? "blue" : "#333333",
            }}
          >
            <CodeIcon />
          </Button>
        )}
        {/* quote */}
        {!useCompactToolbar && (
          <Button
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            title="Quote"
            sx={{
              color: editor.isActive("blockquote") ? "blue" : "#333333",
            }}
          >
            <FormatQuoteIcon />
          </Button>
        )}

        {/* Table button with dimension selector */}
        {!useCompactToolbar && (
          <Button
            onClick={handleTableButtonClick}
            title="Insert Table"
            sx={{
              color: editor.isActive("table") ? "blue" : "#333333",
            }}
          >
            <TableChartIcon />
          </Button>
        )}

        {useCompactToolbar && (
          <>
            <Button
              onClick={handleMoreMenuOpen}
              title="More actions"
              sx={{ color: "#333333" }}
            >
              <MoreHorizIcon />
            </Button>
            <Menu
              anchorEl={moreAnchorEl}
              open={isMoreMenuOpen}
              onClose={handleMoreMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              <MenuItem
                onClick={() => {
                  setOpenImageDialog(true);
                  handleMoreMenuClose();
                }}
              >
                <ListItemIcon>
                  <ImageIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Image</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  editor.chain().focus().toggleCode().run();
                  handleMoreMenuClose();
                }}
              >
                <ListItemIcon>
                  <CodeIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Code</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  editor.chain().focus().toggleBlockquote().run();
                  handleMoreMenuClose();
                }}
              >
                <ListItemIcon>
                  <FormatQuoteIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Quote</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={(event) => {
                  handleTableButtonClick(event as React.MouseEvent<HTMLElement>);
                  handleMoreMenuClose();
                }}
              >
                <ListItemIcon>
                  <TableChartIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Table</ListItemText>
              </MenuItem>
            </Menu>
          </>
        )}

        <Popover
          open={Boolean(tableAnchorEl)}
          anchorEl={tableAnchorEl}
          onClose={handleTablePopoverClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <Box
            sx={{
              p: 1,
              backgroundColor: "white",
            }}
          >
            <Typography
              sx={{
                p: 1,
                fontSize: "14px",
                color: "#666",
              }}
            >
              {hoveredDimensions.rows > 0
                ? `${hoveredDimensions.rows} × ${hoveredDimensions.cols} Table`
                : "Insert Table"}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${maxCols}, 20px)`,
                gap: "2px",
                p: 1,
              }}
            >
              {Array.from({ length: maxRows * maxCols }).map((_, index) => {
                const row = Math.floor(index / maxCols);
                const col = index % maxCols;
                return (
                  <Box
                    key={index}
                    onClick={handleTableInsert}
                    onMouseEnter={() => handleCellHover(row, col)}
                    sx={{
                      width: "20px",
                      height: "20px",
                      border: "1px solid #ddd",
                      backgroundColor:
                        row < hoveredDimensions.rows &&
                        col < hoveredDimensions.cols
                          ? "#e3f2fd"
                          : "white",
                      cursor: "pointer",
                      "&:hover": {
                        border: "1px solid #2196f3",
                      },
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        </Popover>

        {/* humanize button */}

        {/* ask ai button */}
        <Tooltip
          open={showSelectionTooltip}
          title="Please select text first"
          onClose={() => setShowSelectionTooltip(false)}
        >
          <Button
            variant="contained"
            sx={{
              width: "110px",
              height: "36px",
              padding: "6px 14px",
              fontSize: "14px",
              fontWeight: 500,
              mr: "10px",
              color: "#5048E5",
              border: "1px solid #5048E5 !important",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textTransform: "none",
            }}
            disabled={isEEATMode}
            onClick={() => {
              const hasSelection = !editor.state.selection.empty;
              if (!hasSelection) {
                setShowSelectionTooltip(true);
                // Auto-hide tooltip after 2 seconds
                setTimeout(() => setShowSelectionTooltip(false), 2000);
              } else {
                //  call handleAidropdown in articleEditor
                handleAidropdown();
              }
            }}
          >
            <MagicWandIcon
              sx={{
                mt: 0.2,
                color: "#5048E5",
                "& path": { fill: "#5048E5" },
              }}
            />
            Ask AI
          </Button>
        </Tooltip>

        {/* writing mode toggle */}
        {onToggleWritingMode && (
          <Tooltip title={isWritingMode ? "Exit writing mode" : "Writing mode"}>
            <Button
              onClick={onToggleWritingMode}
              title="Toggle writing mode"
              sx={{
                minWidth: "36px",
                width: "36px",
                height: "36px",
                ml: "4px",
                color: isWritingMode ? "#5048E5" : "#333333",
              }}
            >
              {isWritingMode ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </Button>
          </Tooltip>
        )}
      </ButtonGroup>
      <Dialog
        open={openLinkDialog}
        onClose={() => setOpenLinkDialog(false)}
        onKeyDown={(e) => {
          e.stopPropagation();
        }}
      >
        <DialogTitle>Add Link</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="URL"
            type="url"
            fullWidth
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onPaste={(e) => {
              e.stopPropagation();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLinkDialog(false)}>Cancel</Button>
          <Button
            onClick={() => {
              if (linkUrl) {
                editor
                  .chain()
                  .focus()
                  .extendMarkRange("link")
                  .setLink({ href: linkUrl })
                  .run();
              }
              setLinkUrl("");
              setOpenLinkDialog(false);
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openImageDialog}
        onClose={() => setOpenImageDialog(false)}
        onKeyDown={(e) => {
          e.stopPropagation();
        }}
      >
        <DialogTitle>Insert Image</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Image URL"
            type="url"
            fullWidth
            placeholder="https://example.com/image.jpg"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            onPaste={(e) => {
              e.stopPropagation();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenImageDialog(false)}>Cancel</Button>
          <Button
            onClick={() => {
              if (imageUrl) {
                editor.chain().focus().setImage({ src: imageUrl }).run();
              }
              setImageUrl("");
              setOpenImageDialog(false);
            }}
          >
            Insert
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MenuBar;
